import { useMemo } from 'react';

import { chartOfAccountsUtils } from 'app-components/flux/chart-of-accounts';
import type { ChartOfAccountProps } from 'app-components/flux/chart-of-accounts/chartOfAccountsTypes';
import type { SplitProps } from 'app-components/flux/transactions/transactionsTypes';
import type { RecordOf } from 'immutable';
import {
  getExcludedFromReportsDisplayState,
  getExcludedFromSpendingPlanDisplayState,
} from 'components/Transactions/VisibilitySection/utils';

import { transactionDetailsFormHooks } from './formHooks';

type TransactionsById = Parameters<typeof getExcludedFromSpendingPlanDisplayState>[1];

export function useVisibilityDisplayState({
  coa,
  excludeF2S,
  excludeReports,
  hasSplitItemsDefined,
  split,
  transactionsById,
}: {
  coa: ChartOfAccountProps | null | undefined;
  excludeF2S: boolean | null | undefined;
  excludeReports: boolean | null | undefined;
  hasSplitItemsDefined: boolean;
  split: RecordOf<SplitProps> | null;
  transactionsById: TransactionsById;
}): {
  disableReportsValue: boolean;
  visibilityExcludeReportsDisplay: { value: boolean | null; indeterminate: boolean };
  visibilityF2SDisplay: { value: boolean | null; indeterminate: boolean };
} {
  const excludeF2SOverridden = transactionDetailsFormHooks.useSelector(({ state }) => state.excludeF2SOverridden);
  const excludeF2SDisplayFromTxn = useMemo(
    () =>
      getExcludedFromSpendingPlanDisplayState(
        { isExcludedFromF2S: excludeF2S, split: split ?? undefined },
        transactionsById,
      ),
    [excludeF2S, split, transactionsById],
  );
  const excludeF2SDisplay = useMemo(() => {
    if (excludeF2SOverridden) return { value: excludeF2S ?? null, indeterminate: false };
    return excludeF2SDisplayFromTxn;
  }, [excludeF2S, excludeF2SDisplayFromTxn, excludeF2SOverridden]);
  const excludeReportsDisplayFromTxn = useMemo(
    () =>
      getExcludedFromReportsDisplayState(
        { isExcludedFromReports: excludeReports, split: split ?? undefined },
        transactionsById,
      ),
    [excludeReports, split, transactionsById],
  );
  const finalCoa = split ? null : coa;
  const disableReportsValue = finalCoa === null ? false : chartOfAccountsUtils.disableExcludeReports(finalCoa, split);
  const visibilityF2SDisplay = useMemo(
    () => (hasSplitItemsDefined ? excludeF2SDisplayFromTxn : excludeF2SDisplay),
    [excludeF2SDisplay, excludeF2SDisplayFromTxn, hasSplitItemsDefined],
  );
  const visibilityExcludeReportsDisplay = useMemo(
    () =>
      hasSplitItemsDefined ? excludeReportsDisplayFromTxn : { value: excludeReports ?? null, indeterminate: false },
    [excludeReports, excludeReportsDisplayFromTxn, hasSplitItemsDefined],
  );

  return { disableReportsValue, visibilityExcludeReportsDisplay, visibilityF2SDisplay };
}
